import { useState } from 'react';
import { motion } from 'framer-motion';
import { PadlockIcon, TimerIcon, PuzzlePieceIcon, MagnifyingGlassIcon, KeyIcon, CompassIcon } from './EscapeIcons';
import { useLanguage } from '../context/LanguageContext';

function FlipCard({ feat, index }) {
  const [isFlipped, setIsFlipped] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.08 }}
      data-testid={`feature-widget-${index}`}
      className="perspective-[800px] h-40"
      onMouseEnter={() => setIsFlipped(true)}
      onMouseLeave={() => setIsFlipped(false)}
    >
      <motion.div
        className="relative w-full h-full"
        animate={{ rotateY: isFlipped ? 180 : 0 }}
        transition={{ duration: 0.6, ease: [0.4, 0, 0.2, 1] }}
        style={{ transformStyle: 'preserve-3d' }}
      >
        <div
          className="absolute inset-0 bg-card border border-border rounded-2xl p-5 text-center flex flex-col items-center justify-center hover:border-brand/50 transition-colors duration-300"
          style={{ backfaceVisibility: 'hidden' }}
        >
          <div className="w-12 h-12 mx-auto rounded-xl bg-brand/10 flex items-center justify-center mb-3">
            <feat.icon size={22} className="text-brand" />
          </div>
          <h4 className="font-heading text-base tracking-wide uppercase text-foreground">
            {feat.label}
          </h4>
          <p className="font-sans text-xs text-muted-foreground mt-1">
            {feat.detail}
          </p>
        </div>
        <div
          className="absolute inset-0 bg-brand rounded-2xl flex items-center justify-center shadow-[0_0_30px_rgba(227,37,16,0.5)]"
          style={{ backfaceVisibility: 'hidden', transform: 'rotateY(180deg)' }}
        >
          <span className="font-heading text-4xl sm:text-5xl tracking-wider uppercase text-white neon-text-glow">
            {feat.flipWord}
          </span>
        </div>
      </motion.div>
    </motion.div>
  );
}

export default function FeatureWidgets() {
  const { t } = useLanguage();

  const features = [
    { icon: PadlockIcon, label: t('privateRooms'), detail: t('justYourGroup'), flipWord: t('flipWord1') },
    { icon: TimerIcon, label: t('sixtyMinutes'), detail: t('raceClock'), flipWord: t('flipWord2') },
    { icon: PuzzlePieceIcon, label: t('immersivePuzzles'), detail: t('storyClues'), flipWord: t('flipWord3') },
    { icon: MagnifyingGlassIcon, label: t('findClues'), detail: t('searchCorner'), flipWord: t('flipWord4') },
    { icon: KeyIcon, label: t('unlockEscape'), detail: t('earnFreedom'), flipWord: t('flipWord5') },
    { icon: CompassIcon, label: t('navigate'), detail: t('chartCourse'), flipWord: t('flipWord6') },
  ];

  return (
    <section data-testid="feature-widgets-section" className="py-4 sm:py-8 border-b border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 lg:gap-6">
          {features.map((feat, i) => (
            <FlipCard key={i} feat={feat} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
